import { computed } from "@vue/composition-api";
import useMouseSvg from "@/composables/useMouseSvg";
import useFindNeighbors from "./useFindNeighbors";
import annotations from "@/store/modules/annotations";
import ui from "@/store/modules/ui";

export default function useNodes(nodeRef) {
  const nodes = computed(() => annotations.getNodes);
  const nodeNeighbors = computed(() => annotations.neighbors);
  const snapFlag = computed(() => ui.snap);

  const { findAllVHNeighbors: findNeighbors } = useFindNeighbors();

  const place = e => {
    // if not left mouse button, return
    if (e.button !== 0) return;

    const { pos: mousePosSvg } = useMouseSvg(e);
    const newPt = mousePosSvg(e);

    // define new node
    const newNode = {
      selected: false,
      pos: {
        cx: newPt.x,
        cy: newPt.y
      }
    };

    annotations.addNode({ newNode });
  };

  const move = (e, i) => {
    // return if not pressed by the left(main) mouse button
    if (e.button !== 0) return;

    // get svg element (the canvas)
    const elem = e.currentTarget.closest("svg");
    const { pos: mousePosSvg } = useMouseSvg(e);

    // find neighbors to move together with
    const vNeighbors = [];
    const hNeighbors = [];
    // target is included in both lists
    findVHNeighbors(i, vNeighbors, "cx");
    findVHNeighbors(i, hNeighbors, "cy");

    let moving = true;
    let newPt;

    const updateFn = () => {
      // recursively call oneself till mouse is released
      if (moving) requestAnimationFrame(updateFn);

      // translate client space pos to svg space pos
      // and assign
      if (snapFlag.value) {
        vNeighbors.forEach(i => {
          annotations.set({ path: `nodes[${i}].pos.cx`, value: newPt.x });
        });
        hNeighbors.forEach(i => {
          annotations.set({ path: `nodes[${i}].pos.cy`, value: newPt.y });
        });
      } else {
        annotations.set({ path: `nodes[${i}].pos.cx`, value: newPt.x });
        annotations.set({ path: `nodes[${i}].pos.cy`, value: newPt.y });
      }
    };

    // update position
    const moveFn = e => {
      newPt = mousePosSvg(e);
    };
    // remove all listeners after mouse is releaesed
    const stopFn = () => {
      moving = false;
      elem.removeEventListener("mousemove", moveFn);
      elem.removeEventListener("mouseup", stopFn);
    };

    // call update recursively
    requestAnimationFrame(updateFn);
    moveFn(e);

    // update point when moving
    elem.addEventListener("mousemove", moveFn);
    // place point when mouse is released
    elem.addEventListener("mouseup", stopFn);
  };

  const findVHNeighbors = (index, ret, on) => {
    ret.push(index);

    let neighbors = nodeNeighbors.value(index);
    // TODO: probably slow
    neighbors = neighbors.filter(i => ret.indexOf(i) === -1);
    neighbors.forEach(i => {
      if (nodes.value[index].pos[on] === nodes.value[i].pos[on]) {
        findVHNeighbors(i, ret, on);
      }
    });
  };

  const remove = (e, index) => {
    // if not left mouse button, return
    if (e.button !== 0) return;

    annotations.removeNode({ i: index });
  };

  const detectVHNeighbors = (e, index) => {
    return new Promise(resolve => {
      // if not left mouse button, return
      if (e.button !== 0) resolve();
      const neighbors = findNeighbors(index);
      neighbors.forEach(i => {
        annotations.set({ path: `nodes[${i}].selected`, value: true });
      });
      setTimeout(() => {
        neighbors.forEach(i => {
          annotations.set({ path: `nodes[${i}].selected`, value: false });
        });
        resolve();
      }, 2000);
    });
  };

  return {
    nodeRef,
    nodes,
    move,
    place,
    remove,
    detectVHNeighbors
  };
}
